import Button from '@/components/atoms/Button'
import Modal from '@/components/atoms/Modal'
import Textarea from '@/components/atoms/Textarea'
import { cancelMentorshipSchema } from '@/schemas/mentorship.schema'
import mentorshipsService from '@/services/mentorshipsService'
import { modalCreators } from '@/store/ducks/modal'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import inputIsValid from '@/utils/helpers/inputIsValid'
import { Dialog, RadioGroup } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import classNames from 'classnames'
import { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

interface CancelMentorshipProps {
	disabledOnClose?: boolean
}

export interface Option {
	label: string
	value: string
}

type Inputs = {
	cancellationReasonType: string
	reason?: string
}

const optionsTypeReason = [
	{ label: 'Motivos de Trabalho', value: 'WORK_REASON' },
	{ label: 'Imprevistos Pessoais', value: 'PERSONAL_UNFORESEEN' },
	{ label: 'Marcação Duplicada', value: 'MENTORSHIP_DUPLICATION' },
	{
		label: 'Remarcação sem Alinhamento',
		value: 'REMARKING_WITHOUT_PRIOR_ALIGNMENT',
	},
	{ label: 'Outros motivos', value: 'OTHER' },
]

export function CancelMentorship({ disabledOnClose }: CancelMentorshipProps) {
	const {
		modal: { modalInfo },
	} = useAppSelector((state) => state)
	const dispatch = useAppDispatch()

	const [cancellationReasonType, setCancellationReasonType] = useState<{
		label?: string
		value?: string
	}>({})

	const { execute: cancelMentorship, isLoading } =
		mentorshipsService.cancelMentorship()

	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		formState: { errors, touchedFields },
		control,
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(cancelMentorshipSchema),
	})

	const onSubmit = (data: Inputs) => {
		if (data?.cancellationReasonType) {
			cancelMentorship({ ...modalInfo, ...data })
		}
	}

	const onCancellationReasonTypeChange = useCallback(
		(option: Option) => {
			setCancellationReasonType({ label: option.label, value: option.value })
			setValue('cancellationReasonType', option.value as string)
			setValue('reason', undefined)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[setValue]
	)

	async function closeModal() {
		await dispatch(modalCreators.closeModal())
	}

	return (
		<Modal
			name='cancelMentorshipModal'
			disabledOnClose={disabledOnClose}
			className='md:pt-12 md:pb-10 md:px-8 max-w-[560px] border border-white/10'
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className='flex flex-col gap-10'>
					<div className='flex flex-col gap-4'>
						<Dialog.Title
							as='h3'
							className='text-start text-2xl font-medium leading-[108%] text-zinc-50'
						>
							Cancelar mentoria
						</Dialog.Title>
						<div className='flex flex-col gap-6'>
							<div className='flex flex-col'>
								<p className='text-start w-full text-sm font-normal text-white/70 leading-[155%] max-w-[452px]'>
									Selecione o campo abaixo descrevendo o motivo de cancelamento
									de sua mentoria. Como o cancelamento está sendo feito com mais
									de uma hora de antecedência, os créditos utilizados serão
									devolvidos para sua conta – seu saldo será atualizado nas
									próximas horas.
								</p>
							</div>
							<div className='flex flex-col'>
								<Controller
									control={control}
									name='cancellationReasonType'
									render={() => (
										<RadioGroup
											value={cancellationReasonType}
											onChange={onCancellationReasonTypeChange}
											className='flex flex-wrap gap-2 gap-y-2'
										>
											{optionsTypeReason?.map((category, key) => (
												<RadioGroup.Option
													key={key}
													value={category}
													className={({ active, checked }) =>
														twMerge(
															`relative flex max-w-max py-[6px] px-4 rounded-md border dark:border-[#F7F9FB]/25 hover:bg-[#041B3A]/5 dark:hover:bg-[#F7F9FB]/10 text-sm font-medium text-zinc-50 leading-normal cursor-pointer items-center justify-center focus:outline-none transition-colors duration-200`,
															classNames({
																'border-brand-primary hover:border-brand-primary outline-none text-[#F7F9FB] bg-brand-primary hover:bg-brand-primary dark:text-white dark:bg-brand-primary hover:dark:bg-brand-primary dark:bg-opacity-90 dark:font-medium':
																	active || checked,
															})
														)
													}
												>
													{category.label}
												</RadioGroup.Option>
											))}
										</RadioGroup>
									)}
								/>
							</div>
							{getValues('cancellationReasonType') === 'OTHER' && (
								<div>
									<Textarea
										isValid={inputIsValid(errors.reason, touchedFields.reason)}
										{...register('reason')}
										rows={5}
										placeholder='Descreva o motivo do cancelamento...'
									/>
								</div>
							)}
						</div>
					</div>
					<div className='w-full flex flex-col items-center gap-5'>
						<Button
							className='w-full min-w-full h-[48px] rounded-full'
							type='submit'
							isLoading={isLoading}
							disabled={
								!cancellationReasonType?.value ||
								Object.keys(errors).length !== 0
							}
						>
							<p className='ml-2'>Cancelar mentoria</p>
						</Button>
						<button
							className='text-base text-white/70 leading-[140%] font-medium'
							onClick={closeModal}
						>
							Voltar
						</button>
					</div>
				</div>
			</form>
		</Modal>
	)
}
